const APPLICATION_ROLES = new Set(['customer', 'vendor', 'admin', 'super_admin']);
const NON_PRIVILEGED_ROLES = new Set(['customer', 'vendor']);
const ADMIN_ROLES = new Set(['admin', 'super_admin']);
const VENDOR_LEGAL_PATHS = new Set(['/terms', '/privacy']);
const ADMIN_UTILITY_PATHS = new Set([
  '/login',
  '/forgot-password',
  '/reset-password',
  '/auth/callback',
  '/terms',
  '/privacy',
]);

const normalizeRole = (candidate) => {
  const role = String(candidate || '').toLowerCase();
  return APPLICATION_ROLES.has(role) ? role : null;
};

const railsRoleFrom = (profile) => normalizeRole(profile?.rails_profile?.role);

export const preserveTrustedRailsProfile = (currentProfile, incomingProfile, sameUser) => {
  if (!incomingProfile) return null;

  const trustedRailsProfile = incomingProfile.rails_profile || (
    sameUser ? currentProfile?.rails_profile : null
  );

  if (!trustedRailsProfile) return incomingProfile;

  return {
    ...incomingProfile,
    ...trustedRailsProfile,
    rails_profile: trustedRailsProfile,
  };
};

export const applicationRoleFrom = (user, profile) => {
  const railsRole = railsRoleFrom(profile);
  if (railsRole) return railsRole;

  // Supabase metadata can help render non-privileged customer/vendor UI while
  // the Rails profile is unavailable. It must never grant admin access.
  const nonPrivilegedCandidates = [
    user?.app_metadata?.role,
    user?.user_metadata?.role,
    profile?.supabase_profile?.role,
    user?.role,
  ];

  return nonPrivilegedCandidates
    .map(normalizeRole)
    .find((candidate) => NON_PRIVILEGED_ROLES.has(candidate)) || null;
};

export const isApprovedVendorUser = (_user, profile) => railsRoleFrom(profile) === 'vendor';

export const isAdminUser = (_user, profile) => ADMIN_ROLES.has(railsRoleFrom(profile));

export const authenticatedHomePath = (user, profile) => {
  if (isAdminUser(user, profile)) return '/admin-mfa';
  if (isApprovedVendorUser(user, profile)) return '/vendor/dashboard';
  return '/';
};

export const shouldRouteToAdminPortal = (user, profile, pathname = '/') => {
  if (!isAdminUser(user, profile)) return false;

  const path = String(pathname || '/');
  const isAdminPath = path === '/admin-mfa'
    || path === '/admin-panel'
    || path.startsWith('/admin-panel/');

  return !isAdminPath && !ADMIN_UTILITY_PATHS.has(path);
};

export const shouldRouteToVendorPortal = (user, profile, pathname = '/') => {
  if (!isApprovedVendorUser(user, profile)) return false;

  const path = String(pathname || '/');
  const isVendorPortalPath = path === '/vendor' || path.startsWith('/vendor/');

  return !isVendorPortalPath && !VENDOR_LEGAL_PATHS.has(path);
};
